import React from "react";
import { NexOsProvider } from "@/lib/nexOs";
import { FloatingWindowsProvider } from "@/lib/floatingWindows";
import { NdsEditorProvider } from "@/lib/ndsEditor";
import { OpsProvider } from "@/lib/opsCards";
import RecentActivityPanel from "@/components/ops/RecentActivityPanel";
import { DemoModeProvider } from "@/lib/demoMode";
import NdsHeader from "@/components/nds/NdsHeader";
import VerticalSelector from "@/components/nds/VerticalSelector";
import KpiPulseBar from "@/components/nds/KpiPulseBar";
import { useAuth } from "@/lib/auth";
import { isSuperAdminUser } from "@/lib/placement";
import OpsKpiBar from "@/components/nds/OpsKpiBar";
import DemoClicksCard from "@/components/nds/DemoClicksCard";
import LiveTicker from "@/components/nds/LiveTicker";
import { MoneyOpsBar, ProgramPulse } from "@/components/nds/DeskPulse";
import { GrantsKpiRow, PartnersKpiRow } from "@/components/nds/grants/GrantsKpiRow";
import TenStepRevenueFlow from "@/components/nds/TenStepRevenueFlow";
import ImpactFlow from "@/components/nds/ImpactFlow";
import ResidentDashboard from "@/components/nds/ResidentDashboard";
import CapabilityRoadmap from "@/components/nds/CapabilityRoadmap";
import AccordionSection from "@/components/nds/AccordionSection";
import WindowLayer from "@/components/nds/windows/WindowLayer";
import { Section } from "@/lib/dynamicEngine";
import { PartnerWelcomeGate } from "@/components/nds/partnerWelcome/PartnerWelcome";
import UnreadMemoBanner from "@/components/nds/UnreadMemoBanner";

/**
 * NexEnginesCoreLayout — the LOCKED universal template (NDS-1).
 *
 *   Tier 0 · <NdsHeader>          — brand + LIVE + env badge
 *   Tier 0 · <VerticalSelector>   — 3 permanent pill tabs
 *   Tier 1 · <KpiPulseBar>        — 4 metric cards
 *   Tier 1 · <LiveSystemFeed>     — right-rail streaming events
 *   Tier 2 · <TenStepRevenueFlow> — 2×5 grid + coaching modal
 *   Tier 3 · <CapabilityRoadmap>  — 3-col × ~7 rows of capability tiles
 *
 * Every new feature MUST fit inside this shell. Individual sections can be
 * hidden/reordered per operator via /admin/layout (Section scopes: nds_root,
 * nds_kpi, nds_step, nds_capability). Labels & schema editable at /admin/nds
 * and /admin/labels.
 */
export default function NexEnginesCoreLayout({ children, demoMode = false }) {
  const { user } = useAuth();
  const limited = user?.role === "partner_user" || (!isSuperAdminUser(user) && Array.isArray(user?.visible_cards));  // role limits which cards they see: skip the money-wide KPIs too
  return (
    <DemoModeProvider value={demoMode}>
      <NexOsProvider>
        <FloatingWindowsProvider>
          <NdsEditorProvider>
          <OpsProvider>
            <div className="nds-scope w-full" data-testid={demoMode ? "nex-engines-demo-layout" : "nex-engines-core-layout"}>
              {!demoMode && <UnreadMemoBanner />}
              <NdsHeader />
              {user?.role_title === "Resident Leader" && (
                <div className="w-full max-w-[1920px] mx-auto px-6 lg:px-10 pt-3 flex items-center gap-3 text-xs text-white/60" data-testid="leader-desk-bar">
                  <span className="font-mono uppercase tracking-[0.16em] text-[#d4a017]">Resident Leader</span><span className="text-white/25">·</span>
                  <a href="/portal/resident" data-testid="leader-my-portal" className="underline underline-offset-2 hover:text-[#d4a017]">Open my resident portal</a><span className="text-white/25">·</span>
                  <button type="button" data-testid="leader-sign-out" className="underline underline-offset-2 hover:text-[#d4a017]"
                    onClick={() => { localStorage.removeItem("nex_token"); localStorage.removeItem("tea_resident_token"); window.location.href = "/login"; }}>Sign out</button>
                </div>)}
              <VerticalSelector />

              <main className="w-full max-w-[1920px] mx-auto px-6 lg:px-10 py-6 flex flex-col gap-4">
                {/* Tiers 1-3 sit side by side while folded; an open tier takes the full row. */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start" data-testid="nds-tier-row">
                {/* Tier 1 — KPI stack (real-time pulse + platform ops) inside one accordion */}
                {!limited && <Section scope="nds_root" id="tier_1_kpi_feed" className="min-w-0 lg:[&:has([aria-expanded=true])]:col-span-3 lg:[&:has([aria-expanded=true])]:order-last">
                  <AccordionSection id="kpi" kicker="// TIER 1" title="Live KPIs · Real-time pulse + Platform ops">
                    <div className="flex flex-col gap-6">
                      <KpiPulseBar ticker={<LiveTicker />} pulse={demoMode ? null : <ProgramPulse />} />
                      {demoMode ? <OpsKpiBar /> : <MoneyOpsBar />}
                      {isSuperAdminUser(user) && !demoMode && <GrantsKpiRow />}
                      {isSuperAdminUser(user) && !demoMode && <PartnersKpiRow />}
                      <DemoClicksCard />
                    </div>
                  </AccordionSection>
                </Section>}

                {/* Tier 2 — 10-Step Revenue Flow */}
                {!limited && <Section scope="nds_root" id="tier_2_ten_step" className="min-w-0 lg:[&:has([aria-expanded=true])]:col-span-3 lg:[&:has([aria-expanded=true])]:order-last">
                  <AccordionSection id="flow" kicker="// TIER 2" title={demoMode ? "10-Step Revenue Flow · Universal lifecycle" : "10-Step Impact Flow · Resident lifecycle"}>
                    {demoMode ? <TenStepRevenueFlow /> : <ImpactFlow />}
                  </AccordionSection>
                </Section>}

                {/* Tier 3 — Resident Dashboard (Sanctuary pills totaled by engine) */}
                {!demoMode && !(user?.role === "partner_user" && !user?.partner_id) && (
                  <Section scope="nds_root" id="tier_3_ten_step_b" className="min-w-0 lg:[&:has([aria-expanded=true])]:col-span-3 lg:[&:has([aria-expanded=true])]:order-last">
                    <AccordionSection id="flow_b" kicker="// TIER 3" title="Resident Dashboard · Every resident at a glance">
                      <ResidentDashboard />
                    </AccordionSection>
                  </Section>
                )}

                </div>

                {/* Optional child injection (legacy dashboard blocks) */}
                {children && (
                  <Section scope="nds_root" id="tier_2_5_children">
                    <div>{children}</div>
                  </Section>
                )}

                {/* Tier 4 — 20-Capability Roadmap */}
                <Section scope="nds_root" id="tier_3_capabilities">
                  <AccordionSection id="caps" kicker={demoMode ? "// TIER 3" : "// TIER 4"} title="Modular Capabilities · 20-card roadmap">
                    <CapabilityRoadmap />
                  </AccordionSection>
                </Section>

                {!demoMode && <RecentActivityPanel />}

                <footer className="border-t border-white/8 pt-6 mt-4 flex items-center justify-between text-[0.7rem] font-mono tracking-[0.2em] text-nex-muted uppercase">
                  <span>The Exchange Alliance · NDS-1 · Universal Template · v1{demoMode ? " · Sandbox" : ""}</span>
                  <span>© {new Date().getFullYear()} The Exchange Alliance</span>
                </footer>
              </main>

              {/* Floating multi-window layer — non-blocking. */}
              <WindowLayer />
              {!demoMode && <PartnerWelcomeGate user={user} />}
            </div>
          </OpsProvider>
          </NdsEditorProvider>
        </FloatingWindowsProvider>
      </NexOsProvider>
    </DemoModeProvider>
  );
}
